import React, {useRef, useState} from 'react';
import {Link, useLocation, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import InvoiceLayout from "../layouts/InvoiceLayout";
import ButtonLong from "../components/ButtonLong";
import Loading from "../components/Loading";
import {apollo} from "../utils/apollo";
import {CREATE_DIRECT_PAYMENT_LINK, GET_INVOICE} from "../utils/graphql";
import MoneyHighlight from "../components/MoneyHighlight";
import Captcha from '../components/Captcha'
import {useTranslation} from "react-i18next";
import {cn} from "../utils/style";
import Tabby from "../tabby";
import InvoiceField from "../components/InvoiceField";
import InvoiceBanner from "../components/InvoiceBanner";
import PaymentCallback from "../components/PaymentCallback";

function Pay() {
    const {t} = useTranslation();
    const {uuid} = useParams();
    const [showTabbyForm, setShowTabbyForm] = useState(false);
    const recaptcha = useRef()
    const conditionsOfUse = useRef()
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const backendMessage = queryParams.get('message') ?? null;
    const callback = queryParams.get('callback') ?? null;

    const fetchInvoice = useQuery(
        ['invoice', {uuid}],
        async () => {
            const {data} = await apollo.query({
                query: GET_INVOICE, variables: {
                    uuid,
                },
            });
            return data?.getInvoice;
        },
        {
            keepPreviousData: false,
            retry: false
        });
    const invoice = fetchInvoice?.data || null;
    const isSatim = invoice?.gatewayCode === 'satim' || false;
    const createDirectPaymentLinkMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: CREATE_DIRECT_PAYMENT_LINK, variables: {
                    input: {
                        uuid,
                        remarks: "",
                        recaptcha: recaptcha?.current?.getValue() || null,
                        conditions_of_use: conditionsOfUse?.current?.checked || null,
                    }
                },
            });
            return data?.createDirectPaymentLink;
        },
        onSuccess: async (data) => {
            if (!data) return;
            const {method, url, body} = data;

            if (method === 'get' && url) {
                window.location.href = url;
                return;
            }

            if (method === 'post' && url) {

                const parsedData = JSON.parse(body) ?? null;
                if (parsedData) {
                    const form = document.createElement('form');
                    form.method = method;
                    form.name = 'form';
                    form.action = url;
                    for (const key in parsedData) {
                        if (parsedData.hasOwnProperty(key)) {
                            const hiddenField = document.createElement('input');
                            hiddenField.type = 'hidden';

                            hiddenField.name = key;
                            hiddenField.value = parsedData[key];

                            form.appendChild(hiddenField);
                        }
                    }
                    document.body.appendChild(form);
                    try {
                        form.submit();
                    } catch (err) {
                        HTMLFormElement.prototype.submit.call(form);
                    }
                    return;
                }
            }

            alert("Unable to generate payment link. Please try again. Contact support if issue continues.");
        },
        onSettled: async () => {
            recaptcha?.current?.reset()
        }
    })

    if (fetchInvoice.isLoading) return (<InvoiceLayout invoice={invoice}>
        <Loading/>
    </InvoiceLayout>);

    const handlePayButton = () => {
        if (showTabbyForm) {
            // todo: use tabby
        } else {
            createDirectPaymentLinkMutation.mutate();
        }
    }

    if (fetchInvoice.isError) {
        return <InvoiceLayout invoice={invoice}><InvoiceBanner backendMessage={backendMessage} status="unknown_error"/></InvoiceLayout>
    }

    if (fetchInvoice.isSuccess && !invoice) {
        return <InvoiceLayout invoice={invoice}><InvoiceBanner backendMessage={backendMessage}
                                                               status="not_found"/></InvoiceLayout>
    }

    if (fetchInvoice.isSuccess && invoice && callback && ['completed', 'failed', 'canceled'].includes(invoice?.status)) {
        return <PaymentCallback backendMessage={backendMessage} invoice={invoice}/>;
    }

    return (<InvoiceLayout invoice={invoice} test={invoice?.test} showLang={true}>
        {createDirectPaymentLinkMutation.isLoading && <div
            className="z-50 backdrop-blur-sm w-full h-full absolute top-0 left-0 bg-white/20 flex justify-center items-center">
            <Loading message="Redirecting ..."/>
        </div>}

        <InvoiceBanner backendMessage={backendMessage} status={invoice?.status} antiFraud={invoice?.antiFraud}/>

        <div className="bg-white rounded-lg shadow-sm mb-4 mt-3 border border-gray-200 overflow-hidden">
            <div
                className="px-6 py-4 bg-gradient-to-br from-sky-500 to-teal-500 border-b border-gray-200 rounded-t-lg">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-white/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path
                                d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                        </svg>
                    </div>
                    <h2 className="text-lg font-medium text-white">
                        Payment Information
                    </h2>
                </div>
            </div>

            <div className="p-4">
                <div className="flex items-center mb-3 pb-2 border-b border-gray-100">
                    <div className="bg-blue-50 p-1 rounded-full mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 100-12 6 6 0 000 12zm.75-6a.75.75 0 000-1.5h-1.5a.75.75 0 000 1.5h1.5z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                    <h3 className="text-sm font-bold text-gray-700">
                        Merchant Information
                    </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-6">
                    {invoice?.supplier?.displaySupplierDetails ? <>
                        <InvoiceField name="merchant" value={invoice?.supplier?.businessName}
                                      className="col-span-12 sm:col-span-6 bg-blue-50/30 border-blue-100"/>
                        <InvoiceField name="email" value={invoice?.supplier?.email}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name="mobile" value={invoice?.supplier?.phone}
                                      className="col-span-12 sm:col-span-6"/>
                    </> : <>
                        <InvoiceField name="merchant" value={invoice?.merchant?.merchantName}
                                      className="col-span-12 sm:col-span-6 bg-blue-50/30 border-blue-100"/>
                        <InvoiceField name="address" value={invoice?.merchant?.merchantAddress}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name="email" value={invoice?.merchant?.merchantEmail}
                                      className="col-span-12 sm:col-span-6"/>
                        <InvoiceField name="mobile" value={invoice?.merchant?.merchantPhone}
                                      className="col-span-12 sm:col-span-6"/>
                    </>}
                </div>

                <div className="flex items-center mb-3 pb-2 border-b border-gray-100">
                    <div className="bg-blue-50 p-1 rounded-full mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                    <h3 className="text-sm font-bold text-gray-700">
                        Invoice Details
                    </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    <InvoiceField
                        name="amount"
                        value={
                            <div className="flex items-center">
                                <span className="text-lg font-bold text-blue-700">
                                    {invoice?.totalAmount || ''} {invoice?.currency?.symbol || ''}
                                </span>
                            </div>
                        }
                        className="col-span-12 sm:col-span-4 bg-blue-50 border-blue-200"
                    />

                    <InvoiceField
                        name="reference number"
                        value={invoice?.displayReferenceNumber || invoice?.customerReferenceNumber}
                        className="col-span-12 sm:col-span-4 bg-gray-50 border-gray-200"
                    />

                    <InvoiceField
                        name="date"
                        value={invoice?.createdAt}
                        className="col-span-12 sm:col-span-4"
                    />

                    <div className="col-span-12 bg-gray-50/50 rounded-lg p-2 border border-gray-100">
                        <div className="text-xs font-medium text-gray-500 mb-2 uppercase">Customer Information</div>
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                            <InvoiceField name="name" value={invoice?.name}
                                          className="col-span-12 sm:col-span-4 border-0 p-2"/>
                            <InvoiceField name="phone" value={invoice?.phone}
                                          className="col-span-12 sm:col-span-4 border-0 p-2"/>
                            <InvoiceField name="email" value={invoice?.email}
                                          className="col-span-12 sm:col-span-4 border-0 p-2"/>
                        </div>
                    </div>

                    {/*<InvoiceField */}
                    {/*    name="payment type" */}
                    {/*    value={invoice?.splitPayment ? 'Split Payment' : 'Full'} */}
                    {/*    className="col-span-12 sm:col-span-4"*/}
                    {/*/>*/}

                    {isSatim &&
                        <InvoiceField
                            name="Le mode de paiement"
                            value={
                                <div className="flex items-center gap-1.5">
                                    <span className="text-sm">Carte CIB/Edahabia</span>
                                    <img src="/images/satim.png" className="h-5" alt="Carte CIB/Edahabia"/>
                                </div>
                            }
                            className="col-span-12 sm:col-span-8 bg-blue-50/20 border-blue-100"
                        />
                    }
                </div>
            </div>
        </div>

        {!!invoice?.products?.length && (
            <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 bg-white">
                        <thead className="bg-gray-50">
                        <tr>
                            <th scope="col"
                                className="py-2 pl-3 pr-2 text-left text-xs font-semibold uppercase text-gray-700 sm:pl-3">
                                Product
                            </th>
                            <th scope="col"
                                className="px-2 py-2 text-left font-semibold uppercase text-xs text-gray-700">
                                Amount
                            </th>
                            <th scope="col"
                                className="px-2 py-2 text-left font-semibold uppercase text-xs text-gray-700">
                                VAT%
                            </th>
                            <th scope="col"
                                className="px-2 py-2 text-left font-semibold uppercase text-xs text-gray-700">
                                Quantity
                            </th>
                            <th scope="col"
                                className="px-2 py-2 text-left font-semibold uppercase text-xs text-gray-700">
                                Total
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {invoice?.products?.map((product, index) => (
                            <tr
                                key={product.id}
                                className="transition-all duration-300 hover:bg-gray-50"
                            >
                                <td className="py-2 pl-3 pr-2 text-sm font-medium text-gray-900 sm:pl-3">
                                    {product.title}
                                    <div className="text-gray-500 text-xs">{product.details}</div>
                                </td>
                                <td className="px-2 py-2 text-sm text-gray-700">{product.amount} {invoice?.currency?.symbol}</td>
                                <td className="px-2 py-2 text-sm text-gray-700">{product.vat === 0 ? '' : product.vat}</td>
                                <td className="px-2 py-2 text-sm text-gray-700">{product.quantity}</td>
                                <td className="px-2 py-2 text-sm font-medium text-gray-900">{product.total} {invoice?.currency?.symbol || ''}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {showTabbyForm && <Tabby invoice={invoice}/>}

        <div
            className="sticky bottom-0 left-0 w-full bg-white py-4 px-4 border-t border-gray-200 shadow-lg rounded-lg mt-4">
            {(['pending', 'failed'].includes(invoice?.status) && isSatim) && (
                <div className="bg-blue-50 p-3 rounded-lg mb-3 border border-blue-100">
                    <div className="flex mb-2">
                        <Captcha ref={recaptcha}/>
                    </div>
                    <div className="flex gap-x-1 items-center">
                        <input
                            ref={conditionsOfUse}
                            type="checkbox"
                            name="conditionsOfUse"
                            id="conditionsOfUse"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex justify-center items-center gap-x-1">
                            <label htmlFor="conditionsOfUse" className="select-none text-gray-700 text-sm">J'accepte
                                les</label>
                            <Link to="/conditions-of-use-satim" target="_blank"
                                  className="font-semibold text-blue-600 hover:text-blue-800 underline cursor-pointer text-sm">
                                conditions d'utilisation
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg mb-3 border border-gray-100">
                <div className={cn(
                    'flex mb-1',
                    {
                        'gap-x-2 items-end': isSatim,
                        'justify-between items-center': !isSatim,
                    }
                )}>
                    <span className="text-gray-700 font-bold text-base">{t('pay.total')}</span>
                    <MoneyHighlight
                        value={invoice?.totalAmount}
                        currency={invoice?.currency?.symbol}
                        className="!text-2xl !font-bold"
                    />
                </div>
            </div>

            {['pending', 'failed'].includes(invoice?.status) && (
                <div className="flex gap-2 mb-3">
                    <ButtonLong
                        onClick={handlePayButton}
                        className="items-center gap-2 py-3 text-sm font-bold"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                  clipRule="evenodd"/>
                        </svg>
                        {isSatim && <img src="/images/satim.png" className="h-6" alt="Carte CIB/Edahabia"/>}
                        {t('pay.pay_now')}
                    </ButtonLong>
                </div>
            )}

            <div className="flex justify-center border-t border-gray-100 pt-2">
                <div className="flex items-center justify-center space-x-4">
                    <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600 mr-1"
                             viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                  clipRule="evenodd"/>
                        </svg>
                        <span className="text-xs text-gray-600">Secure</span>
                    </div>
                    <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600 mr-1"
                             viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"/>
                        </svg>
                        <span className="text-xs text-gray-600">SSL</span>
                    </div>
                    {/*<div className="flex items-center">*/}
                    {/*    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600 mr-1" viewBox="0 0 20 20" fill="currentColor">*/}
                    {/*        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />*/}
                    {/*        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />*/}
                    {/*    </svg>*/}
                    {/*    <span className="text-xs text-gray-600">PCI</span>*/}
                    {/*</div>*/}
                </div>
            </div>

            {isSatim && (
                <div
                    className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center text-gray-600 justify-between border border-gray-100">
                    <div className="text-xs">En cas de problème, contactez SATIM 3020</div>
                    <img
                        className="shrink-0 w-16"
                        src="/images/3020.svg"
                        alt="SATIM 3020"
                    />
                </div>
            )}
        </div>
    </InvoiceLayout>);
}

export default Pay;
